import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
  IButtonDirective,
  IIconComponent,
  IInputGroupDirective,
  IInputGroupInput,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { StrategyPageService } from "@m19-board/strategies/strategy-page/strategy-page.service";
import { DspAddCustomCreativeModalComponent } from "@m19-board/dsp/dsp-creatives/dsp-custom-creatives/dsp-add-custom-creative-modal/dsp-add-custom-creative-modal.component";
import { AccountSelectionService, DspStrategyService } from "@front/m19-services";
import { CampaignType, DspCreative } from "@front/m19-api-client";
import { filter, switchMap, tap } from "rxjs";
import { DspCustomCreativesTableComponent } from "@m19-board/dsp/dsp-creatives/dsp-custom-creatives/dsp-custom-creatives-table/dsp-custom-creatives-table.component";
import { FormsModule } from "@angular/forms";
import { toSignal } from "@angular/core/rxjs-interop";

@Component({
  selector: "app-dsp-custom-creatives",
  imports: [
    IButtonDirective,
    TranslocoDirective,
    DspCustomCreativesTableComponent,
    FormsModule,
    IInputGroupDirective,
    IInputGroupInput,
    IIconComponent,
  ],
  templateUrl: "./dsp-custom-creatives.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspCustomCreativesComponent {
  private readonly strategyPageService = inject<StrategyPageService<CampaignType.DSP>>(StrategyPageService);
  private readonly dspStrategyService = inject(DspStrategyService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly toasterService = inject(ToasterService);
  private readonly accountSelectionService = inject(AccountSelectionService);

  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { requireSync: true });
  readonly strategy = this.strategyPageService.strategy;
  filter = signal("");
  selectedCreatives = signal<Set<string>>(new Set(), { equal: () => false });
  isDeletingSelectedCreatives = signal(false);

  strategyCreatives = computed<DspCreative[]>(() => {
    const creatives = this.strategy()?.dspCustomCreatives ?? [];
    return creatives.filter((creative) => {
      return (
        creative.name.toLowerCase().includes(this.filter().toLowerCase()) ||
        creative.asin?.toLowerCase().includes(this.filter().toLowerCase())
      );
    });
  });

  addCreative() {
    if (this.isReadOnly()) {
      return;
    }
    this.modalService.openModal(DspAddCustomCreativeModalComponent, {
      size: "xl",
      modalTitle: this.translocoService.translate("dsp-creatives.add_a_creative"),
      height: "fill",
      data: this.strategy(),
    });
  }

  deleteSelectedCreatives() {
    const creativeIdsToDetach = Array.from(this.selectedCreatives());

    this.modalService
      .openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
        modalTitle: this.translocoService.translate("dsp-creatives.delete_selected_creatives"),
        data: {
          message: this.translocoService.translate("dsp-creatives.delete_n_selected_creatives_confirmation", {
            count: creativeIdsToDetach.length,
          }),
          confirmLabel: this.translocoService.translate("common.delete"),
          color: "red",
        },
      })
      .afterClosed()
      .pipe(
        filter((confirmed) => confirmed),
        tap(() => this.isDeletingSelectedCreatives.set(true)),
        switchMap(() =>
          this.dspStrategyService.detachCreativeFromStrategy(this.strategy()!.strategyId!, creativeIdsToDetach),
        ),
      )
      .subscribe({
        next: () => {
          this.isDeletingSelectedCreatives.set(false);
          this.selectedCreatives.update((creatives) => {
            creatives.clear();
            return creatives;
          });
          this.toasterService.success(this.translocoService.translate("dsp-creatives.creatives_successfully_deleted"));
        },
        error: () => {
          this.isDeletingSelectedCreatives.set(false);
          this.toasterService.success(this.translocoService.translate("dsp-creatives.error_deleting_creatives"));
        },
      });
  }
}
